<script setup lang="ts">
	import logoEnlace from '@/assets/imgs/icono_enlace.png'
	import noImage from '@/assets/imgs/no-image.jpg'
	import { useHotelsStore } from '@/stores/hotels'
	import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'
	import { HeartIcon as HeartOutlineIcon } from '@heroicons/vue/24/outline'
	import {
		HeartIcon as HeartSolidIcon,
		SparklesIcon,
	} from '@heroicons/vue/24/solid'
	import { storeToRefs } from 'pinia'
	import { computed, ref } from 'vue'
	import { useToast } from 'vue-toastification'

	import useAuthUser from '@/composables/useAuthUser'
	import { useMarkable } from '@/composables/useMarkable'

	import type { PolicyObjectMap } from '@/interfaces/PolicyObjectMap'

	import usePolicyEvaluator from '../composables/usePolicyEvaluator'
	import { Hotel } from '../interfaces/hotel'
	import { formatProviderDeadline, formatShortDate } from '../utils/hotelDates'
	import PolicyStatusBanner from './PolicyStatusBanner.vue'
	import RateOwnershipConflict from './RateOwnershipConflict.vue'
	import HotelRatingBadge from './HotelRatingBadge.vue'


	interface Props {
		hotel: Hotel
		isMobile: boolean
		displayMap: boolean
		selectedHotel?: Hotel
		reservationNights: number
		isFetching: boolean
		centerActiveHotel: (hotel: Hotel) => void
		openDisclaimerModal: (disclaimer: string) => void
		policyObjectMap?: PolicyObjectMap | null
		daysInAdvance?: number
	}

	interface Emits {
		(event: 'setSelectedHotel', hotel: Hotel): boolean
		(event: 'pushToDetails', hotel: Hotel): boolean
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()
	const { user } = useAuthUser()
	const hotelsStore = useHotelsStore()
	// La agencia decide si el cliente ve el proveedor (panel del monolito); default oculto.
	const { showProviderBadge } = storeToRefs(hotelsStore)
	const changeCurrency = ref(false)
	const isLoadingDetails = ref(false)

	const { removeFavoriteHotelAsync, setFavoriteHotelAsync } = useMarkable()

	const hotelContainer = ref()

	const evaluateSelected = () => props.hotel.id === props.selectedHotel?.id

	const policyContext = computed(() => ({
		totalFare: Number(props.hotel.fareApproximate),
		nights: props.reservationNights,
		daysInAdvance: props.daysInAdvance,
		dailyRate: Number(props.hotel.dailyRateApproximate),
	}))

	const { violations, hardViolations, evaluations } = usePolicyEvaluator(
		computed(() => props.policyObjectMap ?? null),
		policyContext
	)

	const hasHardViolation = computed(() => hardViolations.value.length > 0)

	const centerHotel = () => {
		if (props.displayMap) {
			emits('setSelectedHotel', props.hotel)

			if (!props.isMobile) {
				props.centerActiveHotel(props.hotel)
			}
		}
	}

	const nightFareDisclaimer =
		'El costo por noche es un aproximado; puede variar respecto al desglose de costos al momento de realizar la reserva.'

	defineExpose({ hotelId: props.hotel.id, hotelContainer })

	const isFavorite = ref(props.hotel.favorite)
	const toast = useToast()

	const toggleFavorite = async (id: string, hotel: Hotel) => {
		try {
			isFavorite.value = !isFavorite.value
			hotelsStore.setFavoriteHotel(id, isFavorite.value)
			hotel.favorite = isFavorite.value
			if (!isFavorite.value) {
				await removeFavoriteHotelAsync(id)
				toast.success('Hotel eliminado de favoritos')
			} else {
				await setFavoriteHotelAsync(id)
				toast.success('Hotel agregado a favoritos')
				emits('pushToDetails', hotel)
			}
		} catch (error) {
			console.error('Error al cambiar favorito:', error)
			isFavorite.value = !isFavorite.value
			hotel.favorite = !isFavorite.value
		}
	}

	// `rating` es el del catálogo de booking-internal (el del hotel o, si no tiene, el de
	// su marca); BookingV2 manda 1-5 o null. Ya no viene del proveedor.
	//
	// Rating ≠ calificación de huéspedes, y mezclarlas mentía por los dos lados: a un
	// hostal de 2 estrellas lo etiquetaba "Regular" (juicio de calidad que nadie midió) y a
	// los hoteles de Amadeus les inventaba un conteo de reseñas con Math.random()*2000 — el
	// MISMO hotel salía "2/5 Regular" en la búsqueda y "4.0 (1224)" en el itinerario, donde
	// sí se consulta la calificación real de Google.
	//
	// Aquí sólo se dice el rating del catálogo (HotelRatingBadge), que es un dato curado.
	// Las reseñas reales se quedan en detalle e itinerario: en la lista habría que pedirlas
	// hotel por hotel (300+ llamadas a Places por búsqueda).

	const handleClick = () => {
		changeCurrency.value = !changeCurrency.value
	}

	const handlePushToDetails = () => {
		isLoadingDetails.value = true
		emits('pushToDetails', props.hotel)

		// Reset loading después de un breve delay ya que se abre en nueva pestaña
		setTimeout(() => {
			isLoadingDetails.value = false
		}, 1500)
	}

	const changeTheCurrency = computed(() => {
		if (changeCurrency.value) {
			return `${props.hotel.fare}  ${props.hotel.currency}`
		} else {
			return `${props.hotel.fareApproximate} ${props.hotel.requestedCurrency}`
		}
	})

	const changeTheCurrencyDaily = computed(() => {
		if (changeCurrency.value) {
			return `${props.hotel.dailyRate} ${props.hotel.currency}`
		} else {
			return `${props.hotel.dailyRateApproximate} ${props.hotel.requestedCurrency}`
		}
	})
</script>

<template>
	<li
		ref="hotelContainer"
		:class="[
			'hotel-entry group relative rounded-xl border border-gray-200 bg-white overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-[#021F58]/30',
			displayMap ? 'hover:shadow-lg' : 'hover:-translate-y-1',
			hotel.agreement && 'bg-[#021F58]/5 border-[#021F58]/20',
			hotel.recommendedFavorite && 'bg-yellow-50/50 border-yellow-300',
			evaluateSelected() &&
				displayMap &&
				'!border-[#021F58] !bg-[#021F58]/5 ring-4 ring-[#021F58]/20',
		]"
		class="snap-center"
		@click="centerHotel()"
	>
		<!-- Selected Indicator -->
		<div
			v-if="evaluateSelected() && displayMap"
			class="absolute top-0 left-0 right-0 h-1 bg-[#021F58]"
		></div>

		<article
			:class="[
				'flex gap-0',
				displayMap ? 'flex-col' : 'flex-col min-[580px]:flex-row',
			]"
		>
			<div
				:class="[
					'relative flex items-center flex-shrink-0',
					displayMap ? 'w-full h-32' : 'min-[580px]:w-64',
				]"
			>
				<div class="relative w-full h-full overflow-hidden">
					<img
						v-if="hotel.image"
						:src="hotel.image"
						class="hotel-image group-hover:scale-110 transition-transform duration-500"
					/>
					<img
						v-else
						:src="noImage"
						class="hotel-image group-hover:scale-110 transition-transform duration-500"
					/>
					<!-- Gradient Overlay -->
					<div
						class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
					></div>
				</div>
				<div class="absolute top-2 right-2 z-[5]">
					<button
						v-if="!user!.internal"
						v-tooltip.bottom="
							`${!hotel.favorite ? 'Agregar a favoritos' : 'Quitar de favoritos'}`
						"
						@click.prevent="() => toggleFavorite(hotel.id, hotel)"
						class="rounded-full bg-white/95 backdrop-blur-sm p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 border border-gray-200 relative"
					>
						<HeartOutlineIcon
							v-if="!hotel.favorite"
							class="h-5 w-5 text-gray-500 hover:text-red-500 transition-colors animate-heart-hover"
						/>
						<HeartSolidIcon
							v-else
							class="h-5 w-5 text-red-500 animate-heart"
						/>
					</button>
				</div>
			</div>
			<!-- Hotel info -->
			<div
				:class="[
					'flex flex-1 flex-col',
					displayMap ? 'gap-2 p-3' : 'gap-3 p-4',
				]"
			>
				<div
					:class="['flex gap-4', displayMap ? 'flex-col' : 'justify-between']"
				>
					<div class="flex flex-col gap-2 flex-1">
						<!-- Name with badge -->
						<div class="flex items-start gap-2">
							<div
								v-if="hotel.recommendedFavorite"
								v-tooltip.top="`Recomendado por un miembro de tu organización`"
								class="flex-shrink-0"
							>
								<SparklesIcon
									:class="[
										'text-yellow-500 drop-shadow-md',
										displayMap ? 'h-4 w-4' : 'h-6 w-6',
									]"
								/>
							</div>

							<div class="flex-1 min-w-0">
								<h3
									:class="[
										'font-bold text-gray-900 capitalize leading-tight group-hover:text-[#021F58] transition-colors duration-200 line-clamp-2',
										displayMap ? 'text-sm' : 'text-base sm:text-lg',
									]"
								>
									{{ hotel.name }}
								</h3>
								<!-- La empresa del viajero tiene este hotel (o su cadena, marca o
								grupo) en sus preferidos: con «priorizar» sale primero. -->
								<span
									v-if="hotel.companyPreferred"
									class="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-200"
								>
									<span class="material-symbols-outlined text-[13px]">verified</span>
									Preferido de tu empresa
								</span>
							</div>
						</div>

						<!-- Etiqueta del proveedor: sólo si la agencia la habilitó desde el
						panel (default OCULTA — es nuestra cadena de suministro, no un
						dato que el viajero necesite). -->
						<span
							v-if="hotel.provider && showProviderBadge"
							:class="[
								'mb-1 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
								hotel.provider === 'hotelbeds' ?
									'bg-teal-50 text-teal-700'
								:	'bg-indigo-50 text-indigo-700',
							]"
							>{{
								hotel.provider === 'hotelbeds' ? 'Hotelbeds' : 'Amadeus'
							}}</span
						>

						<!-- Tipo de alojamiento cuando NO es un hotel de habitación privada
						(hostal, apartamento, aparthotel). Ámbar = "ojo, léelo": un
						empleado espera cuarto privado, no una cama en dormitorio. -->
						<span
							v-if="hotel.accommodationTypeName"
							class="mb-1 inline-flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200"
						>
							<span class="material-symbols-outlined text-[12px]">info</span>
							{{ hotel.accommodationTypeName }}
						</span>

						<!-- Cancelación de la tarifa mostrada, con las palabras del detalle. -->
						<span
							v-if="hotel.cancellationStatus === 'free_until' && hotel.cancellationDeadline"
							class="mb-1 inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200"
							:title="`Cancelación sin costo hasta ${formatProviderDeadline(hotel.cancellationDeadline)}`"
						>
							<span class="material-symbols-outlined text-[12px]" aria-hidden="true">event_available</span>
							Cancelación sin costo hasta {{ formatShortDate(hotel.cancellationDeadline) }}
						</span>

						<!-- Rating del catálogo, o la categoría oficial si es Hotelbeds -->
						<HotelRatingBadge
							:rating="hotel.rating"
							:provider="hotel.provider"
							:category="hotel.category"
							:class="displayMap && 'scale-90 origin-left'"
						/>
					</div>

					<div
						:class="[
							'flex flex-col justify-between gap-3',
							displayMap ?
								'border-t-2 pt-2 border-[#021F58]/20'
							:	'border-l-4 border-[#021F58]/20 pl-4 min-[580px]:min-w-[200px]',
						]"
					>
						<div
							:class="[
								'flex gap-3',
								displayMap ?
									'flex-row items-center justify-between'
								:	'flex-col',
							]"
						>
							<!-- Night fare -->
							<div
								:class="[
									'flex gap-1',
									displayMap ? 'flex-row items-center' : 'flex-col items-end',
								]"
							>
								<div class="flex items-center gap-2">
									<p
										:class="[
											'font-extrabold text-[#021F58] whitespace-nowrap',
											displayMap ? 'text-lg' : 'text-2xl',
										]"
									>
										{{ `$${changeTheCurrencyDaily}` }}
									</p>

									<button
										v-if="hotel.requestedCurrency !== hotel.currency"
										@click.stop="handleClick"
										v-tooltip="
											changeCurrency ?
												`Ver precio en ${hotel.requestedCurrency} (Aprox)`
											:	`Ver precio en ${hotel.currency}`
										"
										:class="[
											'rounded-md hover:bg-[#021F58]/5 transition-colors',
											displayMap ? 'p-0.5' : 'p-1',
										]"
									>
										<span
											:class="[
												'material-symbols-outlined text-[#021F58]',
												displayMap ? 'text-base' : 'text-lg',
											]"
										>
											currency_exchange
										</span>
									</button>
								</div>

								<div
									v-if="!displayMap"
									class="flex items-center justify-end"
								>
									<span
										class="flex items-center text-xs text-gray-600 font-medium"
									>
										por noche (sin impuestos)

										<QuestionMarkCircleIcon
											:class="[
												'h-7 w-7 scale-75 text-gray-400 transition-colors duration-500',
												!evaluateSelected() && 'night-fare-disclaimer',
											]"
											aria-hidden="true"
											@click.prevent="openDisclaimerModal(nightFareDisclaimer)"
										/>
									</span>
								</div>
								<span
									v-else
									class="text-xs text-gray-600 font-medium whitespace-nowrap"
								>
									/noche
								</span>
							</div>

							<!-- Total fare -->
							<div
								class="flex flex-col items-end gap-1 bg-gray-50 p-3 rounded-lg"
							>
								<div class="flex items-baseline gap-1">
									<span class="text-xs text-gray-500 font-medium">Total:</span>
									<span class="text-lg font-bold text-gray-900"
										>${{ changeTheCurrency }}</span
									>
								</div>

								<p
									:class="[
										'text-gray-500 leading-tight',
										displayMap ? 'text-[10px] text-left' : 'text-xs text-right',
									]"
								>
									{{
										`${reservationNights} ${reservationNights == 1 ? 'noche' : 'noches'}`
									}}
									<span v-if="displayMap"> • Imp. inc.</span>
								</p>

								<p
									v-if="!displayMap"
									class="text-xs text-gray-500 text-right leading-tight"
								>
									Impuestos incluidos
								</p>
							</div>
						</div>
					</div>
				</div>

				<div
					:class="[
						'mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-gray-100',
						displayMap ? 'pt-2' : 'pt-3',
					]"
				>
					<!-- Agreement indicator -->
					<div
						v-if="hotel.agreement || hotel.companyName"
						class="flex min-w-0 items-center gap-2 flex-1"
					>
						<div
							:class="[
								'flex min-w-0 max-w-full items-center gap-2 rounded-full bg-[#021F58] shadow-md',
								displayMap ? 'px-2 py-1' : 'px-3 py-1.5',
							]"
						>
							<img
								v-if="
									hotel.agreement || hotel.companyIsotipo || hotel.companyLogo
								"
								:src="
									hotel.agreement ? logoEnlace : (
										hotel.companyIsotipo || hotel.companyLogo
									)
								"
								:class="[
									'shrink-0 object-contain rounded-full bg-white p-0.5',
									displayMap ? 'w-4 h-4' : 'w-5 h-5',
								]"
								alt=""
							/>
							<p
								:class="[
									'min-w-0 whitespace-normal break-words font-semibold text-white',
									displayMap ? 'text-[10px]' : 'text-xs',
								]"
							>
								{{
									hotel.agreement ? 'Recomendado' : (
										`Convenio ${hotel.companyName}`
									)
								}}
							</p>
						</div>
					</div>

					<RateOwnershipConflict
						v-if="hotel.rateOwnership?.owner === 'conflict'"
					/>

					<!-- Policy violations compact badges -->
					<PolicyStatusBanner
						v-if="violations.length"
						variant="compact"
						:evaluations="evaluations"
					/>

					<button
						:class="[
							'ml-auto whitespace-nowrap rounded-lg font-semibold text-white shadow-md transition-all duration-200 flex items-center gap-2',
							displayMap ? 'px-3 py-1.5 text-xs' : 'px-6 py-2.5 text-sm',
							isLoadingDetails ? 'bg-[#021F58]/50 cursor-wait'
							: hasHardViolation ? 'bg-gray-400 cursor-not-allowed opacity-60'
							: 'bg-[#021F58] hover:bg-[#1C3F7A] hover:scale-105 hover:shadow-lg',
						]"
						:disabled="isLoadingDetails || hasHardViolation"
						@click.stop="handlePushToDetails"
					>
						<template v-if="isLoadingDetails">
							<svg
								class="animate-spin h-4 w-4 text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								></circle>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
							<span>Abriendo...</span>
						</template>
						<template v-else>
							<span v-if="!displayMap">Ver detalles</span>
							<span v-else>Detalles</span>
							<span
								:class="[
									'material-symbols-outlined',
									displayMap ? 'text-base' : 'text-lg',
								]"
							>
								arrow_forward
							</span>
						</template>
					</button>
				</div>
			</div>
		</article>
	</li>
</template>

<style lang="css" scoped>
	.hotel-image {
		@apply h-48 w-full object-cover object-center min-[580px]:h-full min-[580px]:w-full;
	}

	.hotel-entry:hover .night-fare-disclaimer {
		@apply text-gray-700;
	}

	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	@keyframes hoverpulse {
		100% {
			transform: scale(1);
		}

		50% {
			transform: scale(0.8);
		}

		0% {
			transform: scale(1);
		}
	}

	@keyframes pulse {
		0% {
			transform: scale(1);
		}

		50% {
			transform: scale(1.2);
		}

		100% {
			transform: scale(1);
		}
	}

	.animate-heart {
		animation: pulse 0.5s ease-in-out;
	}

	.animate-heart-hover {
		animation: hoverpulse 0.5s ease-out;
	}
</style>
